import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, Typography, InputAdornment, IconButton, MenuItem, CircularProgress, Autocomplete } from '@mui/material';
import {
  FaIdCard, FaUniversity, FaCalendarAlt, FaUndo, FaBackward, FaBookOpen,
  FaMapMarkedAlt, FaCity, FaGlobeAsia, FaGlobe, FaEnvelope, FaFileUpload, FaTimes
} from 'react-icons/fa';
import PopupModel from '../../popup-model/PopupModel';
import IndianStatesSelect from '../ListOfStates/IndianStates';
import { savePostgraduateDetails, getPostgraduateData, deleteEducation } from '../../../services/candidate/api';
import { useNavigate } from 'react-router-dom';
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from 'react-toastify';
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

const postgraduateCourses = [
  'Master of Business Administration',
  'Master of Computer Applications',
  'M.Tech in Computer Science and Engineering',
  'M.Tech in Mechanical Engineering',
  'M.Tech in Electrical Engineering',
  'M.Tech in Civil Engineering',
  'M.Sc in Physics',
  'M.Sc in Mathematics',
  'M.Sc in Chemistry',
  'M.A in Economics',
  'M.A in Psychology',
  'M.A in English Literature',
  'M.Tech in Biotechnology',
  'M.Sc in Data Science',
  'M.A in Sociology',
  'Other'
];

const StepPostgraduate = ({ data, updateData, onNext, onBack }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    enrollmentNumber: data?.enrollmentNumber || '',
    instituteName: data?.instituteName || '',
    qualification: data?.qualification || 'Postgraduate',
    specialization: data?.specialization || '',
    enrolledYear: data?.enrolledYear || '',
    passingYear: data?.passingYear || '',
    address: data?.address || '',
    city: data?.city || '',
    stateRegionProvince: data?.stateRegionProvince || '',
    country: data?.country || '',
    zipCode: data?.zipCode || '',
  });

  useEffect(() => {
    updateData(form);
  }, [form]);

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showResultPopup, setShowResultPopup] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const certificateFileRef = useRef(null);
  const [educationLevel, setEducationLevel] = useState(null);
  const [error, setError] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [enabledDelete, setEnabledDelete] = useState(false);
  const [isOtherSpecialization, setIsOtherSpecialization] = useState(false);

  const inputRefs = {
    enrollmentNumber: useRef(null),
    instituteName: useRef(null),
    qualification: useRef(null),
    specialization: useRef(null),
    enrolledYear: useRef(null),
    passingYear: useRef(null),
    address: useRef(null),
    city: useRef(null),
    stateRegionProvince: useRef(null),
    country: useRef(null),
    zipCode: useRef(null),
  };

  const fieldOrder = [
    'enrollmentNumber',
    'instituteName',
    'qualification',
    'specialization',
    'enrolledYear',
    'passingYear',
    'address',
    'city',
    'stateRegionProvince',
    'country',
    'zipCode',
  ];

  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop());
    } catch {
      return url.split('/').pop() || 'certificate.pdf';
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getPostgraduateData();
        if (data && Object.keys(data).length > 0) {
          const formatDate = (dateStr) => {
            if (!dateStr) return '';
            if (/^(\d{4}|\d{2}-\d{2}-\d{4}|\d{2}-\d{4})$/.test(dateStr)) {
              return dateStr;
            }
            return '';
          };
          const specialization = data.specialization || '';

          const isOther =
            specialization && !postgraduateCourses.includes(specialization);

          setIsOtherSpecialization(isOther);

          setForm({
            enrollmentNumber: data.enrollmentNumber || '',
            instituteName: data.instituteName || '',
            qualification: data.qualification || 'Postgraduate',
            specialization: data.specialization || '',
            enrolledYear: formatDate(data.enrolledYear),
            passingYear: formatDate(data.passingYear),
            address: data.address || '',
            city: data.city || '',
            stateRegionProvince: data.stateRegionProvince || '',
            country: data.country || '',
            zipCode: data.zipCode || '',
          });
          setCertificate(data.certificateFileUrl ? { url: data.certificateFileUrl, name: getFileNameFromUrl(data.certificateFileUrl) } : null);
          setEducationLevel(data.educationLevel);
          setEnabledDelete(true);
        }
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  const isFormEmpty = () => {
    return (
      !form.enrollmentNumber &&
      !form.instituteName &&
      form.qualification === 'Postgraduate' &&
      !form.specialization &&
      !form.enrolledYear &&
      !form.passingYear &&
      !form.address &&
      !form.city &&
      !form.stateRegionProvince &&
      !form.country &&
      !form.zipCode &&
      !certificate
    );
  };

  const getDateValue = (dateStr) => {
    if (!dateStr || !dateStr.includes('-')) return 0;
    const [month, year] = dateStr.split('-').map(Number);
    return year * 12 + month;
  };
  const validateField = (name, value) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const datePattern = /^(\d{2})-(\d{4})$/;

    switch (name) {
      case 'enrollmentNumber':
        if (!value) {
          return 'Enrollment number is required';
        }
        if (!/^[a-zA-Z0-9 ]{1,20}$/.test(value)) {
          return 'Enrollment number must be alphanumeric and up to 20 characters with no special characters';
        }
        break;
      case 'instituteName':
        if (!value) {
          return 'Institute name is required';
        }
        if (!/^[A-Za-z .'-]{1,50}$/.test(value)) {
          return 'Institute name must contain only alphabets, spaces, dots, or hyphens (1-50 characters)';
        }
        break;
      case 'qualification':
        if (!value) {
          return 'Qualification is required';
        }
        if (value && !/^[A-Za-z ]{3,20}$/.test(value)) {
          return 'Qualification must contain only alphabets (3-20 characters)';
        }
        break;
      case 'specialization':
        if (!value) {
          return 'Specialization is required';
        }
        if (!/^[A-Za-z .]{1,50}$/.test(value)) {
          return 'Specialization must contain only alphabets and spaces (1-50 characters)';
        }
        break;
      case 'enrolledYear':
      case 'passingYear':
        if (!value) {
          return `${name === 'enrolledYear' ? 'Enrolled' : 'Passing'} year is required`;
        } else if (!datePattern.test(value)) {
          return 'Must be in MM-YYYY format';
        } else {
          const [month, year] = value.split('-').map(Number);
          if (month < 1 || month > 12) {
            return 'Month must be between 01 and 12';
          } else if (year < 1900) {
            return 'Year must be after 1900';
          } else if (year > currentYear || (year === currentYear && month > currentMonth)) {
            return 'Cannot be in the future';
          }
        }
        break;
      case 'city':
        if (!value) {
          return 'City is required';
        }
        if (value && !/^[A-Za-z ]{1,25}$/.test(value)) {
          return 'City must contain only alphabets and spaces (1-25 characters)';
        }
        break;
      case 'stateRegionProvince':
        if (!value) {
          return 'State is required';
        }
        if (!/^[A-Za-z ]{1,20}$/.test(value)) {
          return 'State must contain only alphabets and spaces (1-20 characters)';
        }
        break;
      case 'country':
        if (!value) {
          return 'Country is required';
        }
        if (!/^[A-Za-z ]{1,30}$/.test(value)) {
          return 'Country must contain only alphabets and spaces (1-30 characters)';
        }
        break;
      case 'zipCode':
        if (value && !/^\d{6}$/.test(value)) {
          return 'Zip code must be exactly 6 digits';
        }
        break;
      case 'certificate':
        if (!value) {
          return 'Certificate file is required';
        }
        if (value instanceof File) {
          if (value.size > 5 * 1024 * 1024) {
            return 'Certificate file must be less than 5MB';
          }
          if (!['image/jpeg', 'image/png', 'application/pdf'].includes(value.type)) {
            return 'Certificate file must be PDF or image (JPEG/PNG)';
          }
        }
        break;
      default:
        break;
    }
    return '';
  };

  const validateAndSetError = (name, value) => {
    const error = validateField(name, value);
    setValidationErrors((prev) => ({ ...prev, [name]: error }));
  };
  const capitalizeFirstLetter = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === 'specialization') {

      if (value === 'Other') {
        setIsOtherSpecialization(true);
        setForm(prev => ({ ...prev, specialization: '' }));
        return;
      }

      if (isOtherSpecialization) {
        const cleaned = value.replace(/[^A-Za-z ]/g, "").slice(0, 50);
        if (cleaned === '') {
          setIsOtherSpecialization(false);
          setForm(prev => ({ ...prev, specialization: '' }));
          return;
        }

        updatedValue = cleaned;
      } else {
        updatedValue = value;
      }
    }
    if (name === "enrollmentNumber") {
      updatedValue = value.replace(/[^A-Za-z0-9 ]/g, "").slice(0, 20);
    }

    if (name === "zipCode") {
      updatedValue = value.replace(/\D/g, "").slice(0, 6);

    }

    if (name === "instituteName") {
      updatedValue = value.replace(/[^A-Za-z .'-]/g, "").slice(0, 50);
      updatedValue = capitalizeFirstLetter(updatedValue);
    }

    if (name === "address") {
      updatedValue = value.replace(/[^A-Za-z0-9-/ ]/g, "").slice(0, 30);
      updatedValue = capitalizeFirstLetter(updatedValue);
    }

    if (name === "city") {
      updatedValue = value.replace(/[^A-Za-z ]/g, "").slice(0, 25);
      updatedValue = capitalizeFirstLetter(updatedValue);
    }

    if (name === "country") {
      updatedValue = value.replace(/[^A-Za-z ]/g, "").slice(0, 30);
      updatedValue = capitalizeFirstLetter(updatedValue);
    }

    setForm(prev => ({ ...prev, [name]: updatedValue }));
    validateAndSetError(name, updatedValue);

    if (name === 'enrolledYear' || name === 'passingYear') {
      const other = name === 'enrolledYear' ? 'passingYear' : 'enrolledYear';
      validateAndSetError(other, form[other]);
    }
  };

  const handleResetSpecialization = () => {
    setIsOtherSpecialization(false);
    setForm(prev => ({ ...prev, specialization: '' }));
    setValidationErrors(prev => ({ ...prev, specialization: '' }));
  };
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setValidationErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
  };

  const confirmDelete = async () => {

    try {
      setLoadingProfile
        (true);
      const response = await deleteEducation(educationLevel);

      toast.success("Postgraduate details deleted successfully", {
        autoClose: 3000,
      });
      setForm({
        enrollmentNumber: '',
        instituteName: '',
        qualification: 'Postgraduate',
        specialization: '',
        enrolledYear: '',
        passingYear: '',
        address: '',
        city: '',
        stateRegionProvince: '',
        country: '',
        zipCode: '',
      });

      setCertificate(null);
      setEducationLevel(null);
      setEnabledDelete(false);
      setValidationErrors({});
      setShowPopup(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to delete postgraduate details.';

      toast.error(errorMessage, {
        autoClose: 3000,
      });

      setError(errorMessage);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleKeyDown = (e, currentField) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentIndex = fieldOrder.indexOf(currentField);
      const nextField = fieldOrder[currentIndex + 1];
      if (nextField && inputRefs[nextField].current) {
        inputRefs[nextField].current.focus();
      } else {
        certificateFileRef.current.focus();
      }
    }
  };

  const handleCertificateUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      const error = validateField('certificate', file);
      setValidationErrors((prevErrors) => ({ ...prevErrors, certificate: error }));
    }
  };

  const handleRemoveCertificate = () => {
    setCertificate(null);
    setValidationErrors((prevErrors) => ({ ...prevErrors, certificate: validateField('certificate', null) }));
  };

  const cancelDelete = () => {
    setShowPopup(false);
  };
  const handleDelete = () => {
    setShowPopup(true);
  };
  const validateForm = () => {
    const errors = {};
    Object.keys(form).forEach((key) => {
      if (key !== 'certificateFile') {
        const error = validateField(key, form[key]);
        if (error) errors[key] = error;
      }
    });
    const certError = validateField('certificate', certificate);
    if (certError) errors.certificate = certError;
    if (form.enrolledYear || form.passingYear) {
      const enrollVal = getDateValue(form.enrolledYear);
      const passVal = getDateValue(form.passingYear);
      if (enrollVal && passVal && enrollVal >= passVal) {
        errors.enrolledYear = 'Enrolled date must be before passing date';
        errors.passingYear = 'Passing date must be after enrolled date';
      }
    }
    return errors;
  };
  const handleSubmit = async () => {
    if (isFormEmpty()) {
      onNext();
      return;
    }

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmissionResult({
        isSuccess: false,
        title: 'Validation Error',
        message: 'Please fix the errors in the form.',
      });
      setShowResultPopup(true);
      return;
    }

    setLoading(true);
    const payload = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== 'certificateFile' && value) payload.append(key, value);
    });

    try {
      if (certificate instanceof File) {
        payload.append('certificateFile', certificate);
      }
      await savePostgraduateDetails(payload);
      const updatedData = await getPostgraduateData();
      if (updatedData?.certificateFileUrl) {
        setCertificate({
          url: updatedData.certificateFileUrl,
          name: getFileNameFromUrl(updatedData.certificateFileUrl),
        });
      }
      setSubmissionResult({
        isSuccess: true,
        title: 'Success',
        message: 'Postgraduate data saved successfully!',
      });
      setShowResultPopup(true);
      onNext();
    } catch (err) {
      console.error('Error saving postgraduate data:', {
        message: err.message,
        status: err.response?.status,
        data: JSON.stringify(err.response?.data || err, null, 2),
      });
      let errorMessage = err.response?.data?.message || err.message || 'Failed to save postgraduate data';
      if (err.response?.status === 400) {
        errorMessage = 'Invalid data submitted. Please check your inputs and try again.';
        if (err.response?.data?.errors) {
          const backendErrors = {};
          err.response.data.errors.forEach((curr) => {
            backendErrors[curr.field] = curr.defaultMessage;
          });
          setValidationErrors(backendErrors);
        }
      } else if (err.response?.status === 401) {
        errorMessage = 'Unauthorized request. Please ensure you are logged in.';
      } else if (err.response?.status === 503) {
        errorMessage = 'The server is temporarily unavailable. Please try again later.';
      }
      setSubmissionResult({
        isSuccess: false,
        title: 'Submission Error',
        message: errorMessage,
      });
      setShowResultPopup(true);
    } finally {
      setLoading(false);
    }
  };

  const closeResultPopup = () => {
    setShowResultPopup(false);
    setSubmissionResult(null);
  };
  const triggerCertificateUpload = () => {
    certificateFileRef.current.click();
  };

  const handleSpecializationInputChange = (event, newInputValue) => {
    setForm((prevForm) => ({ ...prevForm, specialization: newInputValue }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, specialization: '' }));
  };

  const handleSpecializationChange = (event, newValue) => {
    setForm((prevForm) => ({ ...prevForm, specialization: newValue || '' }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, specialization: '' }));
  };

  const handleSpecializationBlur = (e) => {
    const value = e.target.value;
    const error = validateField('specialization', value);
    setValidationErrors((prevErrors) => ({ ...prevErrors, specialization: error }));
  };

  return (
    <Box className="stepHighSchoolContainer">
      {loading && (
        <Box className="loading-overlay">
          <Box className="loading-content">
            <CircularProgress size={40} style={{ color: '#4D0054' }} />
            <Typography className="loading-text">{loading ? 'Saving...' : 'Loading...'}</Typography>
          </Box>
        </Box>
      )}
      <div className="highschool-header">
        <div className="vertical-line"></div>
        <Typography className="stepHighSchoolTitle">🎓 Postgraduate Details</Typography>
      </div>

      <Box className="stepHighSchoolGrid">
        <TextField
          label="Enrollment Number / Hall Ticket *"
          name="enrollmentNumber"
          value={form.enrollmentNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'enrollmentNumber')}
          inputRef={inputRefs.enrollmentNumber}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.enrollmentNumber}
          helperText={validationErrors.enrollmentNumber}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaIdCard color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Institute Name *"
          name="instituteName"
          value={form.instituteName}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'instituteName')}
          inputRef={inputRefs.instituteName}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.instituteName}
          helperText={validationErrors.instituteName}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Qualification *"
          name="qualification"
          value={form.qualification}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'qualification')}
          inputRef={inputRefs.qualification}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.qualification}
          helperText={validationErrors.qualification}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUniversity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        {!isOtherSpecialization ? (
          <TextField
            select
            label="Specialization *"
            name="specialization"
            value={form.specialization}
            onChange={handleChange}
            fullWidth
            variant="standard"
            error={!!validationErrors.specialization}
            helperText={validationErrors.specialization}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaBookOpen color="#4D0054" />
                </InputAdornment>
              ),
            }}
          >
            {postgraduateCourses.map((course) => (
              <MenuItem key={course} value={course}>
                {course}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            label="Specify Specialization"
            name="specialization"
            value={form.specialization}
            onChange={handleChange}
            fullWidth
            variant="standard"
            error={!!validationErrors.specialization}
            helperText={validationErrors.specialization}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaBookOpen color="#4D0054" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleResetSpecialization} size="small">
                    <FaUndo size={14} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Enrolled Year *"
            views={["year", "month"]}
            format="MM-YYYY"
            disableFuture
            value={form.enrolledYear ? dayjs(form.enrolledYear, "MM-YYYY", true) : null}
            onChange={(newValue) => {
              if (!newValue || !newValue.isValid()) { setForm((prev) => ({ ...prev, enrolledYear: "", })); return; }
              const formatted = newValue.format("MM-YYYY");

              setForm((prev) => ({
                ...prev,
                enrolledYear: formatted,
              }));

              validateAndSetError("enrolledYear", formatted);
            }}
            slotProps={{
              textField: {
                name: "enrolledYear",
                variant: "standard",
                fullWidth: true,
                onBlur: handleBlur,
                error: !!validationErrors.enrolledYear,
                helperText: validationErrors.enrolledYear,
                InputLabelProps: { shrink: true },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaCalendarAlt color="#4D0054" />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />
          <DatePicker
            label="Passing Year *"
            views={["year", "month"]}
            format="MM-YYYY"
            disableFuture
            value={form.passingYear ? dayjs(form.passingYear, "MM-YYYY", true) : null}
            onChange={(newValue) => {
              if (!newValue || !newValue.isValid()) { setForm((prev) => ({ ...prev, passingYear: "", })); return; }
              const formatted = newValue.format("MM-YYYY");

              setForm((prev) => ({
                ...prev,
                passingYear: formatted,
              }));

              validateAndSetError("passingYear", formatted);
            }}
            slotProps={{
              textField: {
                name: "passingYear",
                variant: "standard",
                fullWidth: true,
                onBlur: handleBlur,
                error: !!validationErrors.passingYear,
                helperText: validationErrors.passingYear,
                InputLabelProps: { shrink: true },
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaCalendarAlt color="#4D0054" />
                    </InputAdornment>
                  ),
                },
              },
            }}
          />

        </LocalizationProvider>

        <TextField
          label="Address(optional)"
          name="address"
          value={form.address}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'address')}
          inputRef={inputRefs.address}
          className="stepPostgraduateField stepHighSchoolFull"
          fullWidth
          variant="standard"
          error={!!validationErrors.address}
          helperText={validationErrors.address}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaMapMarkedAlt color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="City *"
          name="city"
          value={form.city}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'city')}
          inputRef={inputRefs.city}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.city}
          helperText={validationErrors.city}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaCity color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />


        <IndianStatesSelect
          value={form.stateRegionProvince}
          onChange={handleChange}
          onBlur={handleBlur}
          error={validationErrors.stateRegionProvince}
          helperText={validationErrors.stateRegionProvince}
          className="stepHighSchoolField"
        />

        <TextField
          label="Country *"
          name="country"
          value={form.country}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'country')}
          inputRef={inputRefs.country}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.country}
          helperText={validationErrors.country}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaGlobe color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Zip Code(optional)"
          name="zipCode"
          value={form.zipCode}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => handleKeyDown(e, 'zipCode')}
          inputRef={inputRefs.zipCode}
          className="stepPostgraduateField"
          fullWidth
          variant="standard"
          error={!!validationErrors.zipCode}
          helperText={validationErrors.zipCode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaEnvelope color="#4D0054" />
              </InputAdornment>
            ),
          }}
        />

        <Box className="upload-container">
          <p className="upload-label-title"><FaFileUpload className="filed-icons" />Postgraduate Certificate*</p>
          <div className="upload-certificate-group">
            <input
              type="file"
              id="certificateUpload"
              ref={certificateFileRef}
              onChange={handleCertificateUpload}
              style={{ display: "none" }}
              accept="image/jpeg,image/png,application/pdf"
            />
            <div
              onClick={triggerCertificateUpload}
              className="upload-certificate-button"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  triggerCertificateUpload();
                }
              }}
              aria-label="Upload certificate file"
            >
              Drag & Drop or Click to Upload
            </div>
            {certificate && (
              <div className="file-list">
                <span className="file-list-item">
                  {certificate instanceof File ? certificate.name : certificate.name || 'Existing File'}
                  <FaTimes
                    className="remove-file-icon"
                    onClick={handleRemoveCertificate}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleRemoveCertificate();
                      }
                    }}
                    aria-label={`Remove ${certificate instanceof File ? certificate.name : certificate.name || 'Existing File'}`}
                  />
                </span>
              </div>
            )}
            {validationErrors.certificate && (
              <Typography color="error" variant="caption">
                {validationErrors.certificate}
              </Typography>
            )}
            <p className="upload-certificate-note">* File must be less than 5MB and must be PDF.</p>
          </div>
        </Box>
      </Box>

      <PopupModel
        show={showResultPopup}
        onClose={closeResultPopup}
        title={submissionResult?.title}
        message={submissionResult?.message}
        isSuccess={submissionResult?.isSuccess}
      />

      <Box className="stepHighSchoolButtonGroup">
        <div>
          {enabledDelete && (
            <button onClick={handleDelete} className="stepHighSchooldelButton" disabled={loading}>
              Delete
            </button>
          )}
        </div>

        <div className="stepHighSchoolrightButtonGroup">
          <div className="button-left">
            <button
              onClick={onBack}
              className="stepHighSchoolButton secondary"
            >
              <FaBackward style={{ marginRight: '6px' }} /> Previous
            </button>
          </div>

          <div className="button-right">
            <button onClick={handleSubmit} className="stepHighSchoolButton" disabled={loading}>
              {loading ? 'Saving...' : 'Next'}
            </button>
          </div>
          {showPopup && (
            <div className="popup-overlay">
              <div className="popup-content">
                <h2 className="popup-title">Confirmation</h2>
                <p>Are you sure you want to delete this Postgraduate record?</p>

                <div className="popup-actions">
                  <button onClick={cancelDelete} className="popup-close-btn error">
                    Cancel
                  </button>
                  <button onClick={confirmDelete} className="popup-close-btn success">
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default StepPostgraduate;